import { type ReactNode } from 'react';
import { useNavGroups, type NavGroup } from './nav-items';
import { type NavItem, SidebarLink } from './sidebar-link';

type Props = {
  isAdmin: boolean;
  isCollector: boolean;
  isVerifying: boolean;
  collapsed: boolean;
  lockAll?: boolean;
  isGameRulesGated?: boolean;
};
type GProps = Props & { group: NavGroup; index: number };

function Separator(): ReactNode {
  return <li className="mx-md my-xs border-t border-white/[0.06]" />;
}

function isHiddenByRole(i: NavItem, p: Props): boolean {
  if (i.adminOnly && !p.isAdmin) return true;
  if (i.collectorOnly && !p.isCollector) return true;
  if (i.hideForCollector && p.isCollector) return true;
  return !!(i.hideForApproacher && !p.isAdmin && !p.isCollector);
}

function isVisible(i: NavItem, p: Props): boolean {
  if (p.lockAll || p.isGameRulesGated) return !isHiddenByRole(i, p);
  // Único enlace para un verifying: Equipo. El hub de /team los reenvía a
  // /team/collectors, que es donde vive su prompt de verificación.
  if (p.isVerifying) return i.to === '/team';
  return !isHiddenByRole(i, p);
}

function GroupItems(gp: GProps): ReactNode {
  const vis = gp.group.items.filter((i) => isVisible(i, gp));
  if (vis.length === 0) return null;
  const disable = gp.lockAll || gp.isGameRulesGated;
  return (
    <>
      {gp.index > 0 && <Separator />}
      {vis.map((item) => (
        <SidebarLink
          key={item.to}
          item={disable ? { ...item, disabled: true } : item}
          isAdmin={gp.isAdmin}
          isCollector={gp.isCollector}
          collapsed={gp.collapsed}
        />
      ))}
    </>
  );
}

export function SidebarNav(p: Props): ReactNode {
  const groups = useNavGroups(p.isAdmin);
  return (
    <nav className="flex-1 px-sm py-sm">
      <ul className="space-y-xs">
        {groups.map((g, i) => (
          <GroupItems key={i} group={g} index={i} {...p} />
        ))}
      </ul>
    </nav>
  );
}
